import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  ambassadorsAll,
  ambassadorRadios,
  ambassadorCriteria,
  ambassadorBenefits,
  ambassadorScoreActions,
  ambassadorDashboardStats,
  getAmbassadorsByRadioId,
  getGlobalAmbassadors,
  getStatusConfig,
  getActiveAmbassadors,
  getDestacadosYleyendas,
  getAmbassadorById,
  getStatusProgress,
  getCriteriaProgress,
  formatAmbassadorCount,
  type AmbassadorEntry,
} from '@/mocks/rfwAmbassadors';

function formatCompact(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function PodiumAmbassadorCard({ ambassador, position }: { ambassador: AmbassadorEntry; position: number }) {
  const statusConfig = getStatusConfig(ambassador.status);
  const medals: Record<number, { gradient: string; label: string; border: string }> = {
    1: { gradient: 'from-emerald-400 to-emerald-600', label: 'ORO', border: 'border-emerald-400' },
    2: { gradient: 'from-amber-300 to-amber-500', label: 'PLATA', border: 'border-amber-300' },
    3: { gradient: 'from-sky-400 to-sky-600', label: 'BRONCE', border: 'border-sky-400' },
  };
  const medal = medals[position];

  return (
    <div className="flex flex-col items-center">
      <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${medal.gradient} text-background-50 text-[10px] font-extrabold tracking-wider mb-2`}>
        {medal.label}
      </div>
      <div className="relative mb-2">
        <img src={ambassador.photoUrl} alt={ambassador.name} className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover object-top border-2 ${medal.border}`} />
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-r ${medal.gradient} flex items-center justify-center text-background-50 text-xs font-bold`}>
          {position}
        </div>
      </div>
      <span className="text-xs font-bold text-foreground-950 text-center leading-tight">{ambassador.name}</span>
      <span className="text-[10px] text-foreground-500">{ambassador.username}</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold mt-1.5 ${statusConfig.bgColor} ${statusConfig.textColor}`}>
        {statusConfig.emoji} {statusConfig.label}
      </span>
      <div className="text-lg md:text-xl font-extrabold text-emerald-600 mt-1">{ambassador.totalScore}</div>
      <span className="text-[10px] text-foreground-400">pts</span>
      <div className="mt-2 flex flex-col gap-0.5 items-center">
        <span className="text-[10px] text-foreground-500">{ambassador.invitaciones} invitaciones</span>
        <span className="text-[10px] text-foreground-500">{ambassador.antiguedad} meses antig.</span>
        <span className="text-[10px] text-foreground-500">{ambassador.reputacion} reputación</span>
      </div>
    </div>
  );
}

function AmbassadorListCard({ ambassador, position }: { ambassador: AmbassadorEntry; position: number }) {
  const statusConfig = getStatusConfig(ambassador.status);
  const isEven = position % 2 === 0;

  return (
    <div className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3.5 ${isEven ? 'bg-background-50' : 'bg-background-50/70'}`}>
      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
        <span className="text-xs font-bold text-foreground-500">#{position}</span>
      </div>
      <img src={ambassador.photoUrl} alt={ambassador.name} className="w-10 h-10 rounded-lg object-cover object-top shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground-950 truncate">{ambassador.name}</span>
          <span className="text-[10px] text-foreground-500">{ambassador.username}</span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.emoji}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-map-pin-line text-foreground-400"></i>{ambassador.city}, {ambassador.country}
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-user-add-line text-foreground-400"></i>{ambassador.invitaciones} invit.
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-calendar-check-line text-foreground-400"></i>{ambassador.antiguedad}m
          </span>
        </div>
        {ambassador.badges.length > 0 && (
          <div className="flex items-center gap-1 mt-1">
            {ambassador.badges.slice(0, 3).map((b, bi) => (
              <span key={bi} className="px-1.5 py-0.5 rounded-full bg-background-200 text-[8px] text-foreground-500">{b}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-bold text-emerald-600">{ambassador.totalScore}</div>
        <div className="text-[10px] text-foreground-400">pts</div>
      </div>
    </div>
  );
}

function CriteriaBreakdownCard({ ambassador }: { ambassador: AmbassadorEntry }) {
  return (
    <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
      <h4 className="text-xs font-bold text-foreground-700 mb-3">Desglose de Criterios</h4>
      <div className="space-y-2">
        {ambassadorCriteria.map((c) => {
          const val = ambassador[c.key as keyof AmbassadorEntry] as number;
          const progress = getCriteriaProgress(ambassador, c.key);
          return (
            <div key={c.key}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <i className={`${c.icon} ${c.color} text-[10px]`}></i>
                  <span className="text-[10px] font-medium text-foreground-700">{c.label}</span>
                </div>
                <span className="text-[10px] font-bold text-foreground-950">{val}</span>
              </div>
              <div className="w-full bg-background-200 rounded-full h-1.5">
                <div className={`h-full rounded-full ${c.color.replace('text-', 'bg-')}`} style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusProgressCard({ ambassador }: { ambassador: AmbassadorEntry }) {
  const progress = getStatusProgress(ambassador);
  const statusConfig = getStatusConfig(ambassador.status);
  return (
    <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-sm ${statusConfig.emoji}`}>{statusConfig.emoji}</span>
          <span className={`text-xs font-bold ${statusConfig.textColor}`}>{statusConfig.label}</span>
        </div>
        {progress.next && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-foreground-500">→ {progress.next}</span>
          </div>
        )}
      </div>
      <div className="w-full bg-background-200 rounded-full h-2 mb-1">
        <div className={`h-full rounded-full transition-all duration-500 ${progress.next ? 'bg-emerald-500' : 'bg-emerald-600'}`} style={{ width: `${progress.percent}%` }}></div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-foreground-500">{ambassador.totalScore} / {progress.next ? (progress.next === 'Leyenda' ? 850 : 600) : '—'} pts</span>
        <span className="text-[9px] font-medium text-emerald-600">{progress.percent}%</span>
      </div>
    </div>
  );
}

export default function EmbajadoresPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [selectedRadioId, setSelectedRadioId] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<'ranking' | 'beneficios' | 'criterios'>('ranking');
  const [selectedAmbassadorId, setSelectedAmbassadorId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'todos' | 'activo' | 'destacado' | 'leyenda'>('todos');

  const ambassadors = useMemo(() => {
    let list = selectedRadioId === null ? getGlobalAmbassadors(30) : getAmbassadorsByRadioId(selectedRadioId);
    if (statusFilter !== 'todos') {
      list = list.filter((a) => a.status === statusFilter);
    }
    return list;
  }, [selectedRadioId, statusFilter]);

  const podium = ambassadors.slice(0, 3);
  const restAmbassadors = ambassadors.slice(3);
  const selectedAmbassador = selectedAmbassadorId ? getAmbassadorById(selectedAmbassadorId) : null;
  const selectedStatusConfig = selectedAmbassador ? getStatusConfig(selectedAmbassador.status) : null;

  const tabs = [
    { id: 'ranking' as const, label: 'Ranking', icon: 'ri-trophy-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
    { id: 'criterios' as const, label: 'Criterios', icon: 'ri-scales-line' },
  ];

  const statusFilters = [
    { id: 'todos' as const, label: 'Todos', icon: 'ri-global-line' },
    { id: 'leyenda' as const, label: 'Leyendas', icon: 'ri-vip-crown-line', color: 'text-rose-600', bgColor: 'bg-rose-100' },
    { id: 'destacado' as const, label: 'Destacados', icon: 'ri-star-line', color: 'text-amber-600', bgColor: 'bg-amber-100' },
    { id: 'activo' as const, label: 'Activos', icon: 'ri-user-heart-line', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-40 md:h-48 bg-gradient-to-br from-emerald-500/20 via-teal-400/15 to-sky-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute -top-14 -right-14 w-60 h-60 rounded-full bg-emerald-400/10 blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-teal-300/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center mb-3">
                  <i className="ri-user-heart-line text-background-50 text-2xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Embajadores RFW</h2>
                <p className="text-sm text-foreground-500">{ambassadorDashboardStats.totalEmbajadores} embajadores · {ambassadorDashboardStats.destacados} Destacados · {ambassadorDashboardStats.leyendas} Leyendas</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-background-200">
              {[
                { label: 'Embajadores', value: ambassadorDashboardStats.totalEmbajadores, icon: 'ri-user-heart-line', color: 'text-emerald-500' },
                { label: 'Leyendas', value: ambassadorDashboardStats.leyendas, icon: 'ri-vip-crown-line', color: 'text-rose-500' },
                { label: 'Destacados', value: ambassadorDashboardStats.destacados, icon: 'ri-star-line', color: 'text-amber-500' },
                { label: 'Activos', value: ambassadorDashboardStats.activosAhora, icon: 'ri-flashlight-line', color: 'text-sky-500' },
                { label: 'Invitaciones', value: formatCompact(ambassadorDashboardStats.totalInvitaciones), icon: 'ri-user-add-line', color: 'text-violet-500' },
                { label: 'Radios', value: ambassadorDashboardStats.radiosNuevas, icon: 'ri-radio-line', color: 'text-teal-500' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* View Tabs */}
          <div className="mt-5 flex items-center justify-center">
            <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === tab.id
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${tab.icon} text-[10px]`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ranking View */}
          {activeView === 'ranking' && (
            <>
              {/* Radio Selector */}
              <div className="mt-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-radio-line text-foreground-500 text-xs"></i>
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">Filtrar por Radio</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedRadioId(null)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedRadioId === null
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                    }`}
                  >
                    <i className="ri-global-line"></i>
                    Global
                  </button>
                  {ambassadorRadios.map((radio) => (
                    <button
                      key={radio.id}
                      onClick={() => setSelectedRadioId(radio.id)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        selectedRadioId === radio.id
                          ? 'bg-foreground-900 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      {radio.name}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        selectedRadioId === radio.id ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
                      }`}>
                        {radio.frequency}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div className="mt-3 flex flex-wrap gap-2">
                {statusFilters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setStatusFilter(f.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === f.id
                        ? 'bg-emerald-500 text-white'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${f.icon} text-[10px]`}></i>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Count info */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-foreground-400">
                  {ambassadors.length} embajadores · {selectedRadioId === null ? 'Global' : ambassadorRadios.find((r) => r.id === selectedRadioId)?.name}
                </span>
              </div>

              {/* Podium */}
              {podium.length >= 3 && (
                <div className="mt-6">
                  <div className="flex items-end justify-center gap-3 md:gap-6 px-2">
                    <PodiumAmbassadorCard ambassador={podium[1]} position={2} />
                    <PodiumAmbassadorCard ambassador={podium[0]} position={1} />
                    <PodiumAmbassadorCard ambassador={podium[2]} position={3} />
                  </div>
                </div>
              )}

              {/* Criterios Score */}
              <div className="mt-8 bg-background-50 rounded-2xl p-5 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-scales-line text-emerald-500"></i>
                  ¿Cómo se Evalúa a un Embajador RFW?
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {ambassadorCriteria.map((c) => (
                    <div key={c.key} className="text-center p-3 rounded-xl bg-background-100">
                      <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                        <i className={`${c.icon} ${c.color} text-sm`}></i>
                      </div>
                      <p className="text-[10px] font-bold text-foreground-950">{c.label}</p>
                      <p className="text-[9px] text-foreground-500">{(c.weight * 100).toFixed(0)}%</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rest of ranking */}
              {restAmbassadors.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-sm font-bold text-foreground-950">Posiciones 4 - {ambassadors.length}</h3>
                  </div>
                  <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70">
                    {restAmbassadors.map((amb, idx) => (
                      <div key={amb.id} onClick={() => setSelectedAmbassadorId(amb.id)} className="cursor-pointer">
                        <AmbassadorListCard ambassador={amb} position={4 + idx} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ambassador Detail Modal */}
              {selectedAmbassador && selectedStatusConfig && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40" onClick={() => setSelectedAmbassadorId(null)}>
                  <div className="bg-background-50 rounded-2xl p-6 w-[440px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-foreground-950">Perfil del Embajador</h3>
                      <button onClick={() => setSelectedAmbassadorId(null)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <img src={selectedAmbassador.photoUrl} alt={selectedAmbassador.name} className="w-20 h-20 rounded-2xl object-cover object-top mx-auto mb-3 border-2 border-background-200" />
                      <p className="text-base font-bold text-foreground-950">{selectedAmbassador.name}</p>
                      <p className="text-xs text-foreground-500">{selectedAmbassador.username} · {selectedAmbassador.city}, {selectedAmbassador.country}</p>
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${selectedStatusConfig.bgColor} ${selectedStatusConfig.textColor}`}>
                          {selectedStatusConfig.emoji} {selectedStatusConfig.label}
                        </span>
                        <span className="text-[10px] text-foreground-400">#{selectedAmbassador.globalRank} Global</span>
                      </div>
                    </div>

                    <StatusProgressCard ambassador={selectedAmbassador} />

                    <div className="mt-4">
                      <CriteriaBreakdownCard ambassador={selectedAmbassador} />
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-background-100 text-center">
                        <p className="text-lg font-bold text-foreground-950">{selectedAmbassador.invitaciones}</p>
                        <p className="text-[9px] text-foreground-500">Invitaciones</p>
                      </div>
                      <div className="p-3 rounded-xl bg-background-100 text-center">
                        <p className="text-lg font-bold text-foreground-950">{selectedAmbassador.antiguedad}m</p>
                        <p className="text-[9px] text-foreground-500">Antigüedad</p>
                      </div>
                      <div className="p-3 rounded-xl bg-background-100 text-center">
                        <p className="text-lg font-bold text-foreground-950">{selectedAmbassador.beneficiosDesbloqueados.length}/4</p>
                        <p className="text-[9px] text-foreground-500">Beneficios</p>
                      </div>
                    </div>

                    {selectedAmbassador.badges.length > 0 && (
                      <div className="mt-4 p-3 rounded-xl bg-background-100">
                        <p className="text-[10px] font-bold text-foreground-700 mb-2">Insignias Obtenidas</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedAmbassador.badges.map((b, bi) => (
                            <span key={bi} className="px-2 py-1 rounded-full bg-background-200 text-[9px] text-foreground-600">{b}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedAmbassador.beneficiosDesbloqueados.length > 0 && (
                      <div className="mt-4 p-3 rounded-xl bg-background-100">
                        <p className="text-[10px] font-bold text-foreground-700 mb-2">Beneficios Desbloqueados</p>
                        <div className="space-y-1.5">
                          {ambassadorBenefits.filter((b) => selectedAmbassador.beneficiosDesbloqueados.includes(b.id)).map((b) => (
                            <div key={b.id} className="flex items-center gap-2">
                              <span className="text-sm">{b.emoji}</span>
                              <span className={`text-[10px] font-medium ${b.textColor}`}>{b.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Beneficios View */}
          {activeView === 'beneficios' && (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ambassadorBenefits.map((benefit) => (
                  <div key={benefit.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl ${benefit.bgColor} flex items-center justify-center shrink-0`}>
                        <span className="text-xl">{benefit.emoji}</span>
                      </div>
                      <div>
                        <h4 className={`text-sm font-bold ${benefit.textColor} mb-1`}>{benefit.name}</h4>
                        <p className="text-[11px] text-foreground-500 leading-relaxed mb-2">{benefit.description}</p>
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-medium ${benefit.bgColor} ${benefit.textColor}`}>
                          {benefit.levels}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Score Actions */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-flashlight-line text-emerald-500"></i>
                  Acciones que Suman Puntos de Embajador
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {ambassadorScoreActions.map((action) => (
                    <div key={action.key} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl ${action.bgColor} border border-transparent`}>
                      <i className={`${action.icon} ${action.color} text-base`}></i>
                      <p className="text-sm font-extrabold text-foreground-950">+{action.points}</p>
                      <p className="text-[9px] text-foreground-600 font-medium text-center">{action.label}</p>
                      <p className="text-[8px] text-foreground-400 text-center">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Criterios View */}
          {activeView === 'criterios' && (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ambassadorCriteria.map((c) => (
                  <div key={c.key} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className={`w-12 h-12 rounded-2xl ${c.bgColor} flex items-center justify-center mb-3`}>
                      <i className={`${c.icon} ${c.color} text-xl`}></i>
                    </div>
                    <h4 className="text-sm font-bold text-foreground-950 mb-1">{c.label}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold ${c.color}`}>{(c.weight * 100).toFixed(0)}%</span>
                      <span className="text-[9px] text-foreground-400">del score total</span>
                    </div>
                    <p className="text-[11px] text-foreground-500 leading-relaxed">{c.description}</p>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                <h3 className="text-sm font-bold text-foreground-950 mb-3">¿Cómo Convertirse en Embajador RFW?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { step: '1', title: 'Sé Activo', desc: 'Participá diariamente en la plataforma. Escuchá radios, comentá, reaccioná. La actividad constante es la base de todo Embajador.', icon: 'ri-flashlight-line', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
                    { step: '2', title: 'Invitá Gente', desc: 'Traé nuevos oyentes y radios a RFW. Cada persona que se une gracias a vos suma puntos y hace crecer la comunidad.', icon: 'ri-user-add-line', color: 'text-violet-600', bgColor: 'bg-violet-100' },
                    { step: '3', title: 'Construí Reputación', desc: 'Interactuá con calidad. Con el tiempo y la constancia, tu reputación crece y te convertís en una Leyenda RFW.', icon: 'ri-shield-check-line', color: 'text-rose-600', bgColor: 'bg-rose-100' },
                  ].map((step) => (
                    <div key={step.step} className="text-center p-4 rounded-xl bg-background-100">
                      <div className={`w-10 h-10 rounded-full ${step.bgColor} flex items-center justify-center mx-auto mb-2`}>
                        <i className={`${step.icon} ${step.color} text-lg`}></i>
                      </div>
                      <p className="text-xs font-bold text-foreground-950 mb-1">{step.step}. {step.title}</p>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center border border-background-200/70">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
              <i className="ri-user-heart-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">¿Querés ser Embajador RFW?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Sé activo, participá en eventos, invitá nuevos oyentes y radios, mantené tu racha y construí reputación. Los Embajadores son los promotores oficiales del crecimiento orgánico de RFW.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/top-fans"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-line mr-1.5"></i>
                Top Fans
              </Link>
              <Link
                to="/seguidores"
                className="px-5 py-2.5 bg-violet-100 hover:bg-violet-200 text-violet-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-1.5"></i>
                Seguidores
              </Link>
            </div>
          </div>

          {/* Cross-links */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/logros" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <i className="ri-medal-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Logros</h4>
                  <p className="text-xs text-foreground-500">Sistema de reconocimiento</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/top-fans" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <i className="ri-star-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Top Fans</h4>
                  <p className="text-xs text-foreground-500">Los más activos</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/compartidos" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                  <i className="ri-share-forward-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Compartidos</h4>
                  <p className="text-xs text-foreground-500">Sistema de referidos</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-8 p-6 rounded-2xl bg-emerald-100/70 border border-emerald-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-user-heart-line text-lg text-white"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Los Embajadores ayudan al crecimiento orgánico de RFW.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              No es un título — es una responsabilidad. Cada Embajador representa a RFW en su comunidad, invita nuevos oyentes y radios, participa en cada evento y construye la reputación de la plataforma. El crecimiento orgánico de RFW depende de sus Embajadores. Son la cara visible de nuestra comunidad en el mundo real.
            </p>
          </div>

        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}